import type { OfflineSyncStatus } from '@/types/offline-recording';
import {
  type EHRAppointment,
  type ScheduleItem,
} from '@heidi/integrations/ehr/contracts';
import { type PatientProfile } from '@heidi/patients/contracts';
import { type SessionStatus } from '@heidi/schema/ml-scribe';
import { type PatientProfileAppointmentCandidates } from '@heidi/session/contracts';

/**
 * Discriminated union for external appointment data.
 * Use the `source` field to narrow the type.
 */
export type ExternalAppointmentData =
  | { source: 'ehr_profile'; data: EHRAppointment }
  | { source: 'integration_schedule'; data: ScheduleItem };

/**
 * Wraps raw EHR appointment or schedule item into the discriminated union type
 */
export function toExternalAppointmentData(
  item: EHRAppointment | ScheduleItem
): ExternalAppointmentData {
  if ('record_id' in item && 'patient_id' in item) {
    return { source: 'ehr_profile', data: item };
  }
  return { source: 'integration_schedule', data: item };
}

/**
 * Type guard to check if appointment is from legacy EHR profile
 */
export function isLegacyEhrAppointment(
  appt: ExternalAppointmentData
): appt is { source: 'ehr_profile'; data: EHRAppointment } {
  return appt.source === 'ehr_profile';
}

/**
 * Type guard to check if appointment is from integration schedule
 */
export function isIntegrationScheduleItem(
  appt: ExternalAppointmentData
): appt is { source: 'integration_schedule'; data: ScheduleItem } {
  return appt.source === 'integration_schedule';
}

export type ConsolidatedSession = {
  isFromEhr: boolean;
  displayName: string;
  session_id?: string | null;
  kinde_gp_id: string | null;
  created_time_local?: string | null;
  externalApptData?: ExternalAppointmentData | null;
  patient_profile?: PatientProfile | null;
  session_status?: SessionStatus | null;
  session_name?: string | null;
  session_gist?: string | null;
  updated_at?: string | null;
  ehr_appt_id?: string | null;
  patient_profile_appointment_candidates?: PatientProfileAppointmentCandidates | null;
  offline_sync_status?: OfflineSyncStatus;
  offline_local_session_id?: string;
  is_offline_synthetic?: boolean;
};
